/**
 * INTERPHONE IA (SACHA) — LE MODÈLE DE L'ÉCRAN (22/10/2026).
 *
 * Types des réponses du relais `/api/v1/interphone/*`, libellés en clair et
 * mise en forme. Fonctions PURES (testées sans navigateur). Le serveur décide
 * (droits, validation des réglages chez le service Interphone) ; l'écran
 * affiche et prévient seulement ce qui ne partirait de toute façon pas.
 */

export type Origine = 'sacha' | 'secours' | 'repertoire'
export type TypeAppel = 'livraison' | 'rendez_vous' | 'employe' | 'artiste' | 'visite' | 'prestataire' | 'objets_trouves' | 'client' | 'urgence' | 'autre' | ''
export type ActionAppel = 'attendre' | 'prevenir' | 'terminer' | ''
export type Equipe = 'bar' | 'technique' | 'direction' | 'equipe' | 'urgence'

export interface PorteStaff { door_id: string; nom: string }

export interface Tableau {
  agent: { en_ligne: boolean; depuis: string; vu_a: string | null; alerte_envoyee: boolean } | null
  aujourdhui: { appels: number; prevenus: number; secours: number }
  dernier_appel: { id: string; debut: string; origine: Origine; type: TypeAppel; action: ActionAppel; resume: string } | null
  soiree: { nom: string; ouverture: string | null; lu_a: string | null } | null
  mode_test: boolean
  porte_staff?: PorteStaff
  /** Absent d'un service plus ancien (23/10/2026) : la carte « Consommation » ne s'affiche pas. */
  conso?: ConsoInterphone
}

/** Ce que l'IA de l'interphone a consommé (Claude pour réfléchir, Mistral pour écouter et parler), estimé en dollars. */
export interface PeriodeConso { appels: number; claude_usd: number; mistral_usd: number; total_usd: number; refus_429: number }
export interface ConsoInterphone { aujourdhui: PeriodeConso; mois: PeriodeConso; tarifs: string; budget_mensuel_usd: number }

export interface AppelResume {
  id: string
  debut: string
  duree_s: number
  origine: Origine
  club_ferme: boolean
  type: TypeAppel
  action: ActionAppel
  equipe: Equipe | ''
  resume: string
  cibles: string
  decroche: boolean | null
  nb_audio: number
  /** Après la sonnerie UniFi (journal d'Access relu par le service) ; absent d'un service plus ancien. */
  suite?: SuiteSonnerie | null
}

export interface SuiteSonnerie {
  etat: 'ouvert' | 'manque' | 'personne'
  par: string
  moyen: string
  delai_s: number | null
  relance: string
  texte: string
}

export interface Voisin { nom: string; phrase: string }

export interface Tour {
  club_ferme?: boolean
  type?: string
  entendu?: string
  dire?: string
  action?: string
  equipe?: string
  resume?: string
  compris_s?: number | null
  reflechi_s?: number | null
  premier_son_s?: number | null
}

export interface FicheAppel {
  appel: AppelResume & { tours: Tour[] }
  audio: { n: number; octets: number; cree: string }[]
  porte_staff: PorteStaff
}

export type Sonnette = 'sacha' | 'repertoire'

/** Vers quelle entrée du Répertoire de l'interphone UniFi Sacha transfère (noms EXACTS des entrées dans Access). */
export interface RepertoireUnifi {
  par_type: Partial<Record<string, string>>
  par_equipe: Record<Equipe, string>
  test: string
}

export interface ReglagesSacha {
  mode_test: boolean
  routage: Record<Equipe, string[]>
  secours: string[]
  transporteurs: string[]
  fournisseurs_bar: string[]
  accueils: { jour: string[]; soir: string[]; nuit: string[]; ferme: string[] }
  infos_manuelles: string
  // Absents d'un service plus ancien : l'écran n'en invente alors aucun.
  sonnette?: Sonnette
  repertoire?: RepertoireUnifi
  /** Coût estimé du mois (dollars) au-delà duquel une alerte part ; 0 = pas d'alerte. */
  budget_mensuel_usd?: number
  /** Voisins avec qui on confond la porte : Sacha dit la phrase et termine. */
  voisins?: Voisin[]
  /** Sacha reçoit les noms du personnel actif et qui a pointé. */
  equipe_connue?: boolean
  /** Entrée du Répertoire relancée une fois si personne ne décroche ; vide = pas de relance. */
  relance?: string
}

export interface ReponseReglages {
  reglages: ReglagesSacha
  maj: string | null
  par: string
  telephones: string[]
  equipes: string[]
  /** Décidé par le serveur : Mode sûr de l'interphone ET droit de régler. */
  modifiable?: boolean
  raison?: string | null
}

export interface Connexion {
  configured: boolean
  api_url: string | null
  token: { set: boolean; updated_at: string | null }
  status: { reachable: boolean; error_code: string | null; message: string | null; agent_en_ligne: boolean | null }
  flux?: { dernier_succes: string | null; echec_depuis: string | null; echec_code: string | null; echec_message: string | null; en_panne: boolean }
}

/** La pastille du raccordement : « Jeton refusé » n'est pas « Injoignable ». */
export function pastilleConnexion(c: Connexion | null): { ton: 'ok' | 'warning' | 'error' | 'idle'; texte: string } {
  if (!c) return { ton: 'idle', texte: '—' }
  if (!c.configured) return { ton: 'idle', texte: 'Non raccordé' }
  if (c.status.error_code === 'INTERPHONE_TOKEN_REFUSED') return { ton: 'error', texte: 'Jeton refusé' }
  if (c.status.error_code === 'INTERPHONE_DISABLED') return { ton: 'idle', texte: 'Coupé (Mode sûr)' }
  if (c.status.reachable) return c.flux?.en_panne ? { ton: 'warning', texte: 'Flux interrompu' } : { ton: 'ok', texte: 'Raccordé' }
  return { ton: 'warning', texte: 'Injoignable' }
}

/** Un appel « récent » : la personne est sans doute encore à la porte. */
export const APPEL_RECENT_MS = 15 * 60_000
export function appelRecent(debut: string | null | undefined, maintenant: Date = new Date()): boolean {
  const d = debut ? Date.parse(debut) : NaN
  return Number.isFinite(d) && maintenant.getTime() - d < APPEL_RECENT_MS && maintenant.getTime() - d >= -60_000
}

/** « lue à HH:MM » quand la soirée a été lue il y a plus d'une heure (sinon rien). */
export function soireeLueA(luA: string | null | undefined, maintenant: Date = new Date()): string | null {
  const d = luA ? Date.parse(luA) : NaN
  if (!Number.isFinite(d) || maintenant.getTime() - d <= 3_600_000) return null
  const q = quandParis(luA, maintenant)
  return q.includes(' ') ? `lue ${q}` : `lue à ${q}`
}

/** Fusionne une page du journal sans doublon (par id), dans l'ordre reçu. */
export function fusionnerAppels(a: AppelResume[], b: AppelResume[]): AppelResume[] {
  const vus = new Set(a.map((x) => x.id))
  return [...a, ...b.filter((x) => !vus.has(x.id) && (vus.add(x.id), true))]
}

/** Le message à montrer pour une lecture audio en échec, d'après le VRAI statut du relais. */
export function messageAudio(status: number, data: unknown): string {
  const m = typeof data === 'object' && data !== null ? (data as { message?: unknown }).message : null
  if (typeof m === 'string' && m.trim()) return m
  if (status === 404) return 'Enregistrement indisponible : la voix est effacée après 30 jours (le texte reste 6 mois).'
  if (status === 403) return 'Vous n’avez pas accès à l’écoute des appels.'
  if (status === 401) return 'Session expirée : reconnectez-vous pour écouter.'
  if (status === 503 || status === 502) return 'Le service Interphone ne répond pas : écoute impossible pour le moment.'
  if (status === 0) return 'Gestion n’a pas répondu.'
  return `Écoute impossible (erreur ${status}).`
}

/* ═════════════════════════════ libellés ═════════════════════════════ */

export const TYPES: Record<string, string> = {
  livraison: 'Livraison', rendez_vous: 'Rendez-vous', employe: 'Employé', artiste: 'Artiste / DJ', visite: 'Visite', prestataire: 'Prestataire',
  objets_trouves: 'Objets trouvés', client: 'Client', urgence: 'Urgence', autre: 'Autre',
}
export const EQUIPES: Record<Equipe, string> = {
  bar: 'Bar', technique: 'Technique', direction: 'Direction', equipe: 'Équipe', urgence: 'Urgence',
}
export const ORDRE_EQUIPES: Equipe[] = ['bar', 'technique', 'direction', 'equipe', 'urgence']
export const ACTIONS: Record<string, string> = { attendre: 'En attente', prevenir: 'Équipe prévenue', terminer: 'Terminé' }
/** Les motifs qu'on peut diriger vers une entrée du Répertoire (un client n'est jamais transféré : Sacha lui répond). */
export const ORDRE_TYPES_TRANSFERT = ['livraison', 'artiste', 'visite', 'prestataire', 'rendez_vous', 'employe', 'objets_trouves', 'urgence', 'autre'] as const
export type TypeTransfert = typeof ORDRE_TYPES_TRANSFERT[number]
export const SONNETTES: { cle: Sonnette; libelle: string; aide: string }[] = [
  { cle: 'sacha', libelle: 'Sacha répond', aide: 'Elle converse, trie, puis fait sonner l’interphone sur la bonne entrée du Répertoire : l’appli UniFi, les écrans et les portables de cette entrée sonnent, la personne qui décroche ouvre.' },
  { cle: 'repertoire', libelle: 'Message « Répertoire »', aide: 'Sacha ne converse pas : un message invite le visiteur à appuyer sur Répertoire puis à choisir le motif de sa visite.' },
]

export const MOMENTS: { cle: keyof ReglagesSacha['accueils']; libelle: string; aide: string }[] = [
  { cle: 'jour', libelle: 'Journée', aide: 'Club ouvert aux livraisons, en journée.' },
  { cle: 'soir', libelle: 'Soirée', aide: 'Avant l’ouverture au public.' },
  { cle: 'nuit', libelle: 'Nuit', aide: 'Pendant la soirée.' },
  { cle: 'ferme', libelle: 'Club fermé', aide: 'Personne sur place : Sacha prend le message.' },
]

/** Bornes du service Interphone (validées chez lui ; rappelées ici pour prévenir avant l'envoi). */
export const BORNES = { telephones: 10, noms: 60, nom: 40, phrases: 15, phrase: 160, infos: 400, entree: 60, budget: 1000,
  voisins: 20, voisin_nom: 40, voisin_phrase: 200 } as const

export const libelleType = (t: string): string => TYPES[t] ?? (t ? t : 'Non classé')
export const libelleEquipe = (e: string): string => (EQUIPES as Record<string, string>)[e] ?? (e || '—')
export const libelleAction = (a: string): string => ACTIONS[a] ?? (a || '—')

/** « UniFi › 3.Technique » = l'interphone a fait sonner une entrée de son Répertoire ; sinon des téléphones SIP. */
export const libelleCibles = (cibles: string): string => (/^(mode test : )?UniFi ›/.test(cibles) ? 'Transfert (Répertoire UniFi)' : 'Téléphones appelés')

export function libelleDecroche(d: boolean | null): string {
  return d === true ? 'Décroché' : d === false ? 'Personne n’a décroché' : '—'
}

/* ═════════════════════════════ dates (Europe/Paris) ═════════════════════════════ */

const HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
const JOUR = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short' })
const JOUR_CLE = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' })

function date(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

export function heureParis(iso: string | null | undefined): string {
  const d = date(iso)
  return d ? HEURE.format(d) : '—'
}

/** « 23:41 » aujourd'hui, « hier 23:41 », sinon « mar. 20 oct. 23:41 » (calendrier de Paris). */
export function quandParis(iso: string | null | undefined, maintenant: Date = new Date()): string {
  const d = date(iso)
  if (!d) return '—'
  const cle = JOUR_CLE.format(d)
  if (cle === JOUR_CLE.format(maintenant)) return HEURE.format(d)
  if (cle === JOUR_CLE.format(new Date(maintenant.getTime() - 86_400_000))) return `hier ${HEURE.format(d)}`
  return `${JOUR.format(d)} ${HEURE.format(d)}`
}

export function duree(s: number | null | undefined): string {
  if (typeof s !== 'number' || !Number.isFinite(s) || s < 0) return '—'
  if (s < 60) return `${Math.round(s)} s`
  return `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, '0')}`
}

export function delai(s: number | null | undefined): string | null {
  if (typeof s !== 'number' || !Number.isFinite(s) || s < 0) return null
  return `${s.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s`
}

/** L'état de Sacha en une phrase. */
export function etatSacha(t: Pick<Tableau, 'agent'>, maintenant: Date = new Date()): { ton: 'ok' | 'ko' | 'gris'; texte: string } {
  if (!t.agent) return { ton: 'gris', texte: 'Sacha ne s’est encore jamais signalée au service.' }
  if (t.agent.en_ligne) return { ton: 'ok', texte: `En ligne depuis ${quandParis(t.agent.depuis, maintenant)}` }
  return { ton: 'ko', texte: `Hors ligne depuis ${quandParis(t.agent.vu_a ?? t.agent.depuis, maintenant)}` }
}

/* ═════════════════════════════ consommation ═════════════════════════════ */

const DEUX_DECIMALES = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** « 1,23 $ » ; « moins de 0,01 $ » plutôt qu'un « 0,00 $ » qui mentirait ; « — » si la valeur manque. */
export function dollars(usd: number | null | undefined): string {
  if (typeof usd !== 'number' || !Number.isFinite(usd) || usd < 0) return '—'
  if (usd === 0) return '0 $'
  if (usd < 0.005) return 'moins de 0,01 $'
  return `${DEUX_DECIMALES.format(usd)} $`
}

/** Où en est le mois par rapport au budget : jamais de « 0 $ » pour un mois où rien n'a encore été mesuré. */
export function etatBudget(c: ConsoInterphone | null | undefined): { ton: 'ok' | 'warning' | 'error' | 'idle'; texte: string; ratio: number | null } {
  if (!c || !c.mois) return { ton: 'idle', texte: '—', ratio: null }
  const budget = typeof c.budget_mensuel_usd === 'number' && Number.isFinite(c.budget_mensuel_usd) ? c.budget_mensuel_usd : 0
  if (!c.mois.appels) return { ton: 'idle', texte: 'Pas encore mesuré ce mois-ci', ratio: null }
  if (budget <= 0) return { ton: 'idle', texte: 'Aucun budget (pas d’alerte)', ratio: null }
  const total = c.mois.total_usd
  if (typeof total !== 'number' || !Number.isFinite(total) || total < 0) return { ton: 'idle', texte: '—', ratio: null }
  const ratio = total / budget
  if (ratio >= 1) return { ton: 'error', texte: 'Budget du mois dépassé', ratio }
  if (ratio >= 0.8) return { ton: 'warning', texte: 'Proche du budget du mois', ratio }
  return { ton: 'ok', texte: 'Dans le budget du mois', ratio }
}

/** « 23/10/2026 » pour la date des tarifs (« 2026-10-23 ») ; « — » pour toute autre forme, jamais une erreur d'écran. */
export function dateTarifs(t: unknown): string {
  return typeof t === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(t) ? t.split('-').reverse().join('/') : '—'
}

/* ═════════════════════════════ réglages : saisie ═════════════════════════════ */

/** Une liste saisie « une par ligne » → liste propre, sans doublon. */
export function lignes(v: string): string[] {
  return [...new Set(v.split('\n').map((x) => x.trim()).filter(Boolean))]
}

export interface SaisieReglages {
  mode_test: boolean
  routage: Record<Equipe, string[]>
  secours: string[]
  transporteurs: string
  fournisseurs_bar: string
  accueils: Record<keyof ReglagesSacha['accueils'], string>
  infos_manuelles: string
  /** null = le service ne connaît pas encore ces réglages : l'écran ne les montre pas et ne les envoie pas. */
  sonnette: Sonnette | null
  repertoire: { par_type: Record<TypeTransfert, string>; par_equipe: Record<Equipe, string>; test: string } | null
  /** Le budget mensuel saisi (dollars, entier) ; null = service plus ancien qui ne le connaît pas. */
  budget_mensuel_usd: string | null
  /** null = service plus ancien qui ne connaît pas ces réglages (04/11/2026). */
  voisins: Voisin[] | null
  equipe_connue: boolean | null
  relance: string | null
}

export function versSaisie(r: ReglagesSacha): SaisieReglages {
  return {
    mode_test: r.mode_test,
    routage: Object.fromEntries(ORDRE_EQUIPES.map((e) => [e, [...(r.routage?.[e] ?? [])]])) as Record<Equipe, string[]>,
    secours: [...(r.secours ?? [])],
    transporteurs: (r.transporteurs ?? []).join('\n'),
    fournisseurs_bar: (r.fournisseurs_bar ?? []).join('\n'),
    accueils: {
      jour: (r.accueils?.jour ?? []).join('\n'), soir: (r.accueils?.soir ?? []).join('\n'),
      nuit: (r.accueils?.nuit ?? []).join('\n'), ferme: (r.accueils?.ferme ?? []).join('\n'),
    },
    infos_manuelles: r.infos_manuelles ?? '',
    sonnette: r.sonnette ?? null,
    repertoire: r.repertoire
      ? {
          par_type: Object.fromEntries(ORDRE_TYPES_TRANSFERT.map((t) => [t, r.repertoire?.par_type?.[t] ?? ''])) as Record<TypeTransfert, string>,
          par_equipe: Object.fromEntries(ORDRE_EQUIPES.map((e) => [e, r.repertoire?.par_equipe?.[e] ?? ''])) as Record<Equipe, string>,
          test: r.repertoire.test ?? '',
        }
      : null,
    budget_mensuel_usd: typeof r.budget_mensuel_usd === 'number' ? String(r.budget_mensuel_usd) : null,
    voisins: Array.isArray(r.voisins) ? r.voisins.map((v) => ({ nom: v.nom, phrase: v.phrase })) : null,
    equipe_connue: typeof r.equipe_connue === 'boolean' ? r.equipe_connue : null,
    relance: typeof r.relance === 'string' ? r.relance : null,
  }
}

/** Le budget saisi en nombre ; NaN si la saisie est invalide (elle reste alors « modifiée » et bloquée par erreursChamps). */
const budgetSaisi = (v: string): number => (/^\d{1,4}$/.test(v.trim()) ? Number(v.trim()) : Number.NaN)

/**
 * La saisie → l'objet COMPLET attendu par le service. `base` garde les clés
 * que cet écran ne connaît pas encore (un réglage ajouté plus tard au service
 * n'est jamais effacé par un enregistrement d'ici).
 */
export function depuisSaisie(s: SaisieReglages, base: ReglagesSacha): ReglagesSacha {
  return {
    ...base,
    mode_test: s.mode_test,
    routage: { ...base.routage, ...Object.fromEntries(ORDRE_EQUIPES.map((e) => [e, [...s.routage[e]]])) } as Record<Equipe, string[]>,
    secours: [...s.secours],
    transporteurs: lignes(s.transporteurs),
    fournisseurs_bar: lignes(s.fournisseurs_bar),
    accueils: {
      ...base.accueils,
      jour: lignes(s.accueils.jour), soir: lignes(s.accueils.soir), nuit: lignes(s.accueils.nuit), ferme: lignes(s.accueils.ferme),
    },
    infos_manuelles: s.infos_manuelles.trim(),
    ...(s.sonnette && base.sonnette !== undefined ? { sonnette: s.sonnette } : {}),
    ...(s.budget_mensuel_usd !== null && base.budget_mensuel_usd !== undefined ? { budget_mensuel_usd: budgetSaisi(s.budget_mensuel_usd) } : {}),
    ...(s.voisins !== null && base.voisins !== undefined
      ? { voisins: s.voisins.map((v) => ({ nom: v.nom.trim(), phrase: v.phrase.trim() })).filter((v) => v.nom || v.phrase) } : {}),
    ...(s.equipe_connue !== null && base.equipe_connue !== undefined ? { equipe_connue: s.equipe_connue } : {}),
    ...(s.relance !== null && base.relance !== undefined ? { relance: s.relance.trim() } : {}),
    ...(s.repertoire && base.repertoire
      ? {
          repertoire: {
            ...base.repertoire,
            // un motif vide = « selon l'équipe » : il n'est pas envoyé (les motifs que l'écran ne montre pas sont gardés)
            par_type: {
              ...Object.fromEntries(Object.entries(base.repertoire.par_type ?? {}).filter(([t]) => !(ORDRE_TYPES_TRANSFERT as readonly string[]).includes(t))),
              ...Object.fromEntries(ORDRE_TYPES_TRANSFERT.map((t) => [t, s.repertoire!.par_type[t].trim()]).filter(([, v]) => v)),
            },
            par_equipe: Object.fromEntries(ORDRE_EQUIPES.map((e) => [e, s.repertoire!.par_equipe[e].trim()])) as Record<Equipe, string>,
            test: s.repertoire.test.trim(),
          },
        }
      : {}),
  }
}

/**
 * Ce qui ne partirait pas, CHAMP PAR CHAMP (la raison s'affiche près du champ).
 * Clés : `routage.<équipe>`, `secours`, `transporteurs`, `fournisseurs_bar`,
 * `accueils.<moment>`, `infos_manuelles`, `budget_mensuel_usd`. Le service tranche ensuite.
 */
export function erreursChamps(s: SaisieReglages): Record<string, string> {
  const out: Record<string, string> = {}
  for (const e of ORDRE_EQUIPES) {
    if (s.routage[e].length > BORNES.telephones) out[`routage.${e}`] = `${EQUIPES[e]} : ${BORNES.telephones} téléphones au plus.`
  }
  if (s.secours.length > BORNES.telephones) out['secours'] = `Secours : ${BORNES.telephones} téléphones au plus.`
  for (const [cle, nom] of [['transporteurs', 'Transporteurs'], ['fournisseurs_bar', 'Fournisseurs du bar']] as const) {
    const l = lignes(s[cle])
    const long = l.find((x) => x.length > BORNES.nom)
    if (l.length > BORNES.noms) out[cle] = `${nom} : ${BORNES.noms} noms au plus.`
    else if (long) out[cle] = `${nom} : « ${long.slice(0, 30)}… » dépasse ${BORNES.nom} caractères.`
  }
  for (const m of MOMENTS) {
    const l = lignes(s.accueils[m.cle])
    if (l.length === 0) out[`accueils.${m.cle}`] = `Accueil « ${m.libelle} » : il faut au moins une phrase.`
    else if (l.length > BORNES.phrases) out[`accueils.${m.cle}`] = `Accueil « ${m.libelle} » : ${BORNES.phrases} phrases au plus.`
    else if (l.some((x) => x.length > BORNES.phrase)) out[`accueils.${m.cle}`] = `Accueil « ${m.libelle} » : chaque phrase fait ${BORNES.phrase} caractères au plus.`
  }
  if (s.infos_manuelles.trim().length > BORNES.infos) out['infos_manuelles'] = `Infos manuelles : ${BORNES.infos} caractères au plus.`
  if (s.budget_mensuel_usd !== null) {
    const b = budgetSaisi(s.budget_mensuel_usd)
    if (!Number.isFinite(b) || b > BORNES.budget) out['budget_mensuel_usd'] = `Budget mensuel : un nombre entier de 0 à ${BORNES.budget} dollars (0 = pas d’alerte).`
  }
  if (s.repertoire) {
    const entree = (v: string, cle: string, nom: string, obligatoire: boolean) => {
      const t = v.trim()
      if (!t) { if (obligatoire) out[cle] = `${nom} : indiquez une entrée du Répertoire.`; return }
      if (t.length > BORNES.entree) out[cle] = `${nom} : ${BORNES.entree} caractères au plus.`
      else if (/[\u0000-\u001f\u007f]/.test(t)) out[cle] = `${nom} : caractère invisible interdit.`
    }
    for (const e of ORDRE_EQUIPES) entree(s.repertoire.par_equipe[e], `repertoire.equipe.${e}`, `Équipe ${EQUIPES[e]}`, true)
    for (const t of ORDRE_TYPES_TRANSFERT) entree(s.repertoire.par_type[t], `repertoire.type.${t}`, `Motif ${TYPES[t]}`, false)
    entree(s.repertoire.test, 'repertoire.test', 'Entrée de test', false)
  }
  if (s.relance !== null) {
    const t = s.relance.trim()
    if (t.length > BORNES.entree) out['relance'] = `Relance : ${BORNES.entree} caractères au plus.`
    else if (/[\u0000-\u001f\u007f]/.test(t)) out['relance'] = 'Relance : caractère invisible interdit.'
  }
  if (s.voisins !== null) {
    const remplis = s.voisins.filter((v) => v.nom.trim() || v.phrase.trim())
    const vus = new Set<string>()
    if (remplis.length > BORNES.voisins) out['voisins'] = `Voisins : ${BORNES.voisins} au plus.`
    for (const v of remplis) {
      const nom = v.nom.trim(), phrase = v.phrase.trim()
      if (nom.length < 2 || nom.length > BORNES.voisin_nom) { out['voisins'] = `Voisin « ${nom || '?'} » : un nom de 2 à ${BORNES.voisin_nom} caractères.`; break }
      if (phrase.length < 5 || phrase.length > BORNES.voisin_phrase) { out['voisins'] = `Voisin « ${nom} » : une phrase de 5 à ${BORNES.voisin_phrase} caractères.`; break }
      if (vus.has(nom.toLowerCase())) { out['voisins'] = `Voisin « ${nom} » en double.`; break }
      vus.add(nom.toLowerCase())
    }
  }
  return out
}

/** La première raison (Save Bar) ; null = rien à redire. */
export function invalide(s: SaisieReglages): string | null {
  return Object.values(erreursChamps(s))[0] ?? null
}

/** Les téléphones proposés pour une liste : ceux du standard, plus ceux déjà choisis qui n'y seraient plus. */
export function choixTelephones(telephones: string[], choisis: string[]): { tel: string; inconnu: boolean }[] {
  const out = telephones.map((tel) => ({ tel, inconnu: false }))
  for (const tel of choisis) if (!telephones.includes(tel)) out.push({ tel, inconnu: true })
  return out
}

/* ═════════════════════════════ chemins ═════════════════════════════ */

export const cheminFiche = (id: string): string => `/securite/interphone-ia/appels/${encodeURIComponent(id)}`
export const cheminAudio = (id: string, n: number): string => `/api/v1/interphone/appels/${encodeURIComponent(id)}/audio/${n}`

/** Le message d'une réponse en erreur du relais (`{ error, message }`), sinon un repli honnête. */
export function messageErreur(status: number, data: unknown, repli: string): string {
  const m = typeof data === 'object' && data !== null ? (data as { message?: unknown }).message : null
  if (typeof m === 'string' && m.trim()) return m
  if (status === 403) return 'Vous n’avez pas accès à cette fonction.'
  if (status === 0) return 'Gestion n’a pas répondu.'
  return repli
}
